import { ProductImage as ProductImageType } from '@aboutyou/legacy-shared/src/types/images'
import { getCdnProductImageUrl } from '@aboutyou/legacy-shared/src/utils/images'
import { TrackingPromotionCreative } from '@aboutyou/legacy-tracking-sdk'
import { SortingChannels } from 'constants/sorting'
import { cdnImageUrl } from 'helpers/image'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { useProductClickTracking } from 'helpers/tracking/hooks/trackingCollection'
import { useTrackFeature } from 'helpers/tracking/hooks/useTrackFeature'
import {
  ProductImpression,
  StoryPromotionTracker,
} from 'helpers/tracking/trackerComponents/ElementTracker'
import { useObserver } from 'mobx-react-lite'
import React, {
  FC,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useService, useStoryById } from 'services/utils/context'
import { createProductStreamFetcher } from 'sharedComponents/ProductStream/loadingHelpers'
import { useStoryPageStore } from 'store/context'
import Product from 'store/entities/Product/Product.store'
import PaginationViewStore from 'store/sidecar/PaginationViewStore'
import ProductImage from 'uiComponents/molecules/ProductImage/ProductImage'
import StoryTile from 'uiComponents/molecules/StoryTile/StoryTile'
import StoryTileCTAContext from './StoryTileCTAContext'

const ESTIMATED_IMG_SIZE_REGULAR_SMALL_SCREEN = 400
const ESTIMATED_IMG_SIZE_REGULAR_LARGE_SCREEN = 600

interface Props {
  storyId: string
  importance?: 'high' | 'low'
  largeTile?: boolean
  onLinkClick?: () => void
}

const StoryTileConnected = forwardRef<HTMLDivElement, Props>(
  function StoryTileConnected(
    { storyId, importance, largeTile = false, ...props },
    ref,
  ) {
    const story = useStoryById(storyId)
    const { products, onMouseEnter } = useProducts(story?.categoryId!)
    const trackFeature = useTrackFeature()

    const trackDirectAddToBasket = useCallback(
      (productId: string) => {
        trackFeature(
          'story_cluster_direct_add_to_basket',
          `${story?.id ?? ''}|${productId}`,
          null,
        )
      },
      [story, trackFeature],
    )

    const baseImageWidth = largeTile
      ? 2 * ESTIMATED_IMG_SIZE_REGULAR_SMALL_SCREEN
      : ESTIMATED_IMG_SIZE_REGULAR_SMALL_SCREEN

    const highResImageWidth = largeTile
      ? 2 * ESTIMATED_IMG_SIZE_REGULAR_LARGE_SCREEN
      : ESTIMATED_IMG_SIZE_REGULAR_LARGE_SCREEN

    const storyImage = cdnImageUrl(story?.mobileImage!, {
      width: baseImageWidth,
      quality: 70,
    })
    const retinaStoryImage = cdnImageUrl(story?.mobileImage!, {
      width: highResImageWidth,
      quality: 70,
    })

    const productTiles = useMemo(
      () =>
        products.slice(0, largeTile ? 7 : 5).map(product => (
          <ElementTrackingContextProvider
            key={product.id}
            id={Number(product.id)}
            type="product"
          >
            <TrackedProductImage
              product={product}
              trackFeature={trackDirectAddToBasket}
            />
          </ElementTrackingContextProvider>
        )),
      [largeTile, products, trackDirectAddToBasket],
    )

    return (
      <StoryTile
        ref={ref}
        href={story?.url!}
        title={story?.headline!}
        subTitle={story?.subline!}
        src={storyImage}
        srcSet={`${storyImage} ${baseImageWidth}w,${retinaStoryImage} ${highResImageWidth}w`}
        onMouseEnter={onMouseEnter}
        importance={importance}
        productTiles={productTiles}
        largeTile={largeTile}
        {...props}
      />
    )
  },
)

const StoryTileConnectedWithTracking: FC<
  Props & {
    indexInSection?: number
  }
> = ({ indexInSection, storyId, ...props }) => (
  <ElementTrackingContextProvider
    key={storyId}
    id={Number(storyId)}
    type="story"
    indexInSection={indexInSection}
  >
    <StoryPromotionTracker<HTMLDivElement>
      cachePageImpression
      storyId={storyId}
      source={TrackingPromotionCreative.StoryReco}
    >
      {({ onClickCapture, ref }) => (
        <StoryTileConnected
          ref={ref}
          storyId={storyId}
          onLinkClick={() => {
            onClickCapture()
          }}
          {...props}
        />
      )}
    </StoryPromotionTracker>
  </ElementTrackingContextProvider>
)

const useProducts = (categoryId: number) => {
  const [products, setProducts] = useState<Product[]>([])
  const storyPage = useStoryPageStore()
  const sortingScore = useObserver(() => storyPage.sorting.score)
  const service = useService()

  const fetchProducts = useCallback(
    createProductStreamFetcher({
      sortId: 'topseller',
      sortChannel: SortingChannels.UnSponsoredDefault,
      categoryId: String(categoryId),
      service,
      score: sortingScore,
    }),
    [categoryId, sortingScore],
  )

  const onMouseEnter = useCallback(async () => {
    if (products.length) {
      return
    }
    const { products: fetchedProducts } = await fetchProducts({
      perPage: PaginationViewStore.productsPerBatch,
    })
    setProducts(fetchedProducts.filter(product => product.isActive))
  }, [fetchProducts, products])

  return {
    onMouseEnter,
    products,
  }
}

const TrackedProductImage = ({
  product,
  trackFeature,
}: {
  product: Product
  trackFeature: (productId: string) => void
}) => {
  const ctaContext = useContext(StoryTileCTAContext)
  const trackedProductClick = useProductClickTracking({
    productId: product.id,
    onClick: () => {
      ctaContext?.onProductClick(product)
      trackFeature(product.id)
    },
  })
  const productImage: ProductImageType = {
    ...product.defaultImage!,
    hash: product.defaultImage?.id!,
  }

  return (
    <ProductImpression<HTMLDivElement | HTMLButtonElement>
      cachePageImpression
      productId={product.id}
    >
      {({ ref }) => (
        <ProductImage
          ref={ref}
          key={product.id}
          dataTestId="ProductImage"
          padded
          hoverEffect
          src={getCdnProductImageUrl(productImage, { width: 220 })}
          onClick={trackedProductClick}
          as="button"
        />
      )}
    </ProductImpression>
  )
}

export default StoryTileConnectedWithTracking
